import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Download, PencilLine, Search, Trash } from "lucide-react";
import { toast } from "react-toastify";

import Loading from "../components/common/LoaderCommon";
import Modal from "../components/common/Modal";
import PaginationComponent from "../components/PaginationComponent";
import ConfirmPopup from "../components/ConfirmPopup";
import { Button } from "../utils/components/ui/button";
import { DropdownMenu } from "@radix-ui/react-dropdown-menu";
import AddNewProduct from "../components/forms/AddNewProduct";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../utils/components/ui/dropdown-menu";
import useProductRequestStore from "../store/useProductStore";
import {
  useDelProduct,
  useGetAProduct,
  useDownloadAllProducts,
  useGetAllProductData,
  useUpdateProductQuantity,
} from "../service/productService";
import { stockTypes } from "../utils/constants/constants";
import Permission from "../components/auth/Permission";
import { ProductT } from "../types/productTypes";

// function StatCard({
//   title,
//   value,
//   subtitle,
//   icon,
// }: {
//   title: string;
//   value: number;
//   subtitle: string;
//   icon: React.ReactNode;
// }) {
//   return (
//     <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-[0_14px_36px_rgba(26,94,249,0.07)]">
//       <div className="flex items-start justify-between gap-4">
//         <div>
//           <p className="text-sm font-medium text-[#7B8797]">{title}</p>
//           <p className="mt-3 text-[28px] font-semibold tracking-[-0.02em] text-[#1A2B49]">
//             {value}
//           </p>
//           <p className="mt-2 text-sm text-[#8D98A7]">{subtitle}</p>
//         </div>
//         <div className="rounded-[16px] bg-[#EEF4FF] p-3 text-[#1A5EF9]">
//           {icon}
//         </div>
//       </div>
//     </div>
//   );
// }

function QuantityEditor({ product }: { product: ProductT }) {
  const [isEditing, setIsEditing] = useState(false);
  const [qty, setQty] = useState(product.qty);
  const { mutate: updateQuantity, isPending } = useUpdateProductQuantity();
  const queryClient = useQueryClient();

  const handleSave = () => {
    updateQuantity(
      { productId: product.id, quantity: qty },
      {
        onSuccess: () => {
          toast.success("Quantity updated");
          setIsEditing(false);
          queryClient.invalidateQueries({ queryKey: ["useGetAllProduct"] });
        },
        onError: (err: Error) => toast.error(err.message),
      },
    );
  };

  if (!isEditing) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span className="text-sm text-[#344338]">
          {product.stockDisplay?.label ||
            `${product.qty} ${product.baseUnit || product.unit || ""}`}
        </span>
        <Permission roles={["admin", "manager"]}>
          <button
            onClick={() => setIsEditing(true)}
            className="text-[#6B7C98] transition hover:text-[#1A5EF9]"
            title="Edit Quantity"
          >
            <PencilLine className="size-3" />
          </button>
        </Permission>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <input
        type="number"
        value={qty}
        onChange={(e) => setQty(Number(e.target.value))}
        className="w-16 rounded border border-[#D7E2F5] px-1 py-1 text-sm outline-none focus:border-[#BFD1FF]"
      />
      <button
        onClick={handleSave}
        disabled={isPending}
        className="rounded bg-[#1A5EF9] px-2 py-1 text-xs text-white hover:bg-[#0E4FDE] disabled:opacity-50"
      >
        Save
      </button>
      <button
        onClick={() => {
          setIsEditing(false);
          setQty(product.qty);
        }}
        className="rounded border border-[#D7E2F5] bg-white px-2 py-1 text-xs text-[#495B78] hover:bg-[#F5F8FD]"
      >
        Cancel
      </button>
    </div>
  );
}

function Product() {
  const queryClient = useQueryClient();
  const { setCurrentProduct } = useProductRequestStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const [rackLabel, setRackLabel] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  const { mutate: deleteProduct } = useDelProduct();
  const { mutate: fetchProductById } = useGetAProduct();
  const { mutate: downloadAllProducts } = useDownloadAllProducts();

  const { data, isLoading } = useGetAllProductData({
    productName: searchKey,
    pageSize,
    pageNo: currentPage,
    stockStatus: status,
    categoryId: null,
    brandId: null,
    keyword: null,
    rackLabel: rackLabel || null,
  });

  const totalPages = data?.totalPages || 0;
  const productData = data?.productResponseList || [];
  const totalElements = data?.totalElements || 0;

  // const inStockCount = useMemo(
  //   () =>
  //     productData.filter((product) => product.stockStatus === "INS_STOCK")
  //       .length,
  //   [productData],
  // );
  // const outOfStockCount = useMemo(
  //   () =>
  //     productData.filter((product) => product.stockStatus !== "INS_STOCK")
  //       .length,
  //   [productData],
  // );
  // const totalUnits = useMemo(
  //   () =>
  //     productData.reduce(
  //       (total, product) => total + Number(product.qty || 0),
  //       0,
  //     ),
  //   [productData],
  // );

  const handleEdit = (product) => {
    fetchProductById(product.id, {
      onSuccess: (data) => {
        setCurrentProduct(data);
        setIsModalOpen(true);
      },
      onError: (error: Error) => {
        console.error("Failed to fetch product", error);
        toast.error(`Failed to fetch product: ${error.message}`);
      },
    });
  };

  const handleDelete = (id: string | undefined) => {
    deleteProduct(id as string, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["useGetAllProduct"] });
        toast.success("Product deleted successfully");
      },
      onError: (error: Error) => {
        console.error("Error deleting product", error);
        toast.error(error.message);
      },
    });
    setConfirmOpen(false);
  };

  const handleDownload = (docType: "PDF" | "EXCEL") => {
    downloadAllProducts({
      isDownload: true,
      docType,
    });
  };

  const currentStatusLabel =
    status === null
      ? "All Status"
      : stockTypes.find((option) => option.id === status)?.name || "Status";

  return (
    <div className="min-h-full py-1">
      <div className="space-y-6">
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                Product Management
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                Track product availability, pricing, and catalog quality with a
                cleaner listing experience and faster actions.
              </p>
            </div>
            <Permission roles={["admin", "manager"]}>
              <div className="flex flex-wrap gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="h-11 rounded-[12px] border border-[#BFD1FF] bg-white px-6 text-[#1A5EF9] hover:bg-[#EEF4FF]">
                      <Download className="mr-2 size-4" />
                      Download All
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="min-w-[150px]">
                    <DropdownMenuItem
                      className="cursor-pointer font-medium text-[#43536A]"
                      onClick={() => handleDownload("PDF")}
                    >
                      As PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer font-medium text-[#43536A]"
                      onClick={() => handleDownload("EXCEL")}
                    >
                      As Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
                  onClick={() => {
                    setIsModalOpen(true);
                    setCurrentProduct(null);
                  }}
                >
                  Add Product
                </Button>
              </div>
            </Permission>
          </div>

          {/* <div className="mt-6 grid gap-4 md:grid-cols-3">
            <StatCard
              title="Visible Products"
              value={totalElements}
              subtitle="Products matching your current filters"
              icon={<Box className="size-5" />}
            />
            <StatCard
              title="In Stock"
              value={inStockCount}
              subtitle="Products currently available for sales flow"
              icon={<ShieldCheck className="size-5" />}
            />
            <StatCard
              title="Units on Page"
              value={totalUnits}
              subtitle="Combined quantity from the visible result set"
              icon={<PackageCheck className="size-5" />}
            />
          </div> */}
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative min-w-[280px] flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
                <input
                  type="text"
                  placeholder="Search a product..."
                  onChange={(e) => {
                    setSearchKey(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
                <input
                  type="text"
                  placeholder="Search rack label..."
                  onChange={(e) => {
                    setRackLabel(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>

              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-12 min-w-[180px] rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-left text-sm font-medium text-[#5D6E8A] transition hover:border-[#BFD1FF]"
                      type="button"
                    >
                      {currentStatusLabel}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="min-w-[180px]">
                    {stockTypes.map((option) => (
                      <DropdownMenuItem
                        key={option.id?.toString()}
                        onSelect={() => setStatus(option.id as string)}
                      >
                        {option.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {status !== null && (
                  <button
                    className="text-sm font-medium text-[#D14343] transition hover:text-[#AF2B2B]"
                    onClick={() => {
                      setStatus(null);
                      setCurrentPage(0);
                    }}
                    title="Clear status filter"
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998]">
              <span className="font-semibold text-[#253650]">
                {totalElements}
              </span>{" "}
              products found
            </div>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#21314A]">
                Product Directory
              </h2>
              <p className="mt-1 text-sm text-[#7D8998]">
                Review product pricing, units, stock position, and brand details
                from a cleaner listing layout.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#84928C]">Rows per page:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="rounded-[10px] border border-[#D7E2F5] bg-white px-3 py-2 text-sm font-medium text-[#495B78] transition hover:bg-[#F5F8FD]"
                      type="button"
                    >
                      {pageSize}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {[10, 20, 30, 50, 100].map((size) => (
                      <DropdownMenuItem
                        key={size}
                        onSelect={() => {
                          setPageSize(size);
                          setCurrentPage(0);
                        }}
                      >
                        {size}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-6 py-4">Image</th>
                    <th className="px-6 py-4">Product Name</th>
                    <th className="px-6 py-4">Keyword</th>
                    <th className="px-6 py-4 text-right">Price</th>
                    <th className="px-6 py-4 text-right">Last Purchase Cost</th>
                    <th className="px-6 py-4 text-right">Unit</th>
                    {/* <th className="px-6 py-4 text-right">Brand</th> */}
                    <th className="px-6 py-4">Stock</th>
                    <th className="px-6 py-4 text-right">Qty</th>
                    <Permission roles={["admin", "manager"]}>
                      <th className="px-6 py-4 text-center">Action</th>
                    </Permission>
                  </tr>
                </thead>
                <tbody>
                  {productData.length > 0 ? (
                    productData.map((product) => (
                      <tr
                        key={product.id}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                      >
                        <td className="px-6 py-4">
                          <img
                            src={product.imageUrl || "/images/product.png"}
                            alt=""
                            className="h-10 w-10 rounded-xl object-cover ring-1 ring-[#DFE8F8]"
                          />
                        </td>
                        <td className="px-6 py-4 font-semibold text-[#24344F]">
                          {product.productName}
                        </td>
                        <td className="px-6 py-4 text-[#6D7C93]">
                          {product.keyword}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                          {product.price}
                        </td>
                        <td className="px-6 py-4 text-right text-[#6D7C93]">
                          {(() => {
                            if (
                              product.unitOptions &&
                              product.unitOptions.length > 0
                            ) {
                              return (
                                <div className="flex flex-col items-end gap-1">
                                  {product.unitOptions.map((opt, i) => (
                                    <span
                                      key={i}
                                      className="whitespace-nowrap text-xs"
                                    >
                                      {Number(opt.unitPrice || 0).toFixed(3)}/
                                      {opt.unit}
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                            return product.latestPurchasePrice != null
                              ? product.latestPurchasePrice.toFixed(3)
                              : "-";
                          })()}
                        </td>
                        <td className="px-6 py-4 text-right">{product.unit}</td>
                        {/* <td className="px-6 py-4 text-right">
                          {product.brand?.brandName ?? "N/A"}
                        </td> */}
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              product.stockStatus === "INS_STOCK"
                                ? "bg-[#EAF2FF] text-[#1A5EF9]"
                                : product.stockStatus === "LOW_STOCK"
                                  ? "bg-[#FFF1E6] text-[#C76A12]"
                                  : "bg-[#FDEEEE] text-[#D14343]"
                            }`}
                          >
                            {product.stockStatus === "INS_STOCK"
                              ? "In Stock"
                              : product.stockStatus === "LOW_STOCK"
                                ? "Low Stock"
                                : "Out of Stock"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-medium">
                          <Permission roles={["USER", "MANAGER"]}>
                            <span className="text-sm text-[#344338]">
                              {product.stockDisplay?.label ||
                                `${product.qty} ${product.baseUnit || product.unit || ""}`}
                            </span>
                          </Permission>

                          <Permission roles={["ADMIN"]}>
                            <QuantityEditor product={product} />
                          </Permission>
                        </td>
                        <td className="px-6 py-4">
                          <Permission roles={["admin", "manager"]}>
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() => handleEdit(product)}
                                title="Edit product"
                                aria-label="Edit product"
                                className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                              >
                                <PencilLine className="size-4" />
                                <span className="sr-only">Edit</span>
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteId(product.id + "");
                                  setConfirmOpen(true);
                                }}
                                title="Delete product"
                                aria-label="Delete product"
                                className="rounded-[10px] border border-[#F5D4D4] p-2 text-[#B95A5A] transition hover:border-[#EAA7A7] hover:text-[#D14343]"
                              >
                                <Trash className="size-4" />
                                <span className="sr-only">Delete</span>
                              </button>
                            </div>
                          </Permission>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                      >
                        No products match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <Modal open={isModalOpen} setOpen={setIsModalOpen}>
          <AddNewProduct setOpen={setIsModalOpen} />
        </Modal>
      )}
      {confirmOpen && (
        <ConfirmPopup
          open={confirmOpen}
          setOpen={setConfirmOpen}
          onConfirm={() => handleDelete(deleteId)}
          title="Delete Product"
          message="Are you sure you want to delete this product?"
        />
      )}
    </div>
  );
}

export default Product;
